import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import IconButton from "../../../../../components/base/button/icon_button";
import {Box, Chip, Typography} from "@mui/material";
import {Col, Row} from 'reactstrap';
import SummaryInfoCard from "../../../../../components/app-specific/summary-cards/summary-info-card";
import {Add, ArrowBack, DeleteOutlined, Edit} from "@mui/icons-material";
import NcrNoteCard from "../../../../../components/app-specific/ncr-cards/ncr-note-card";
import NCRInformationActionTable from "./action-table";
import Button from "../../../../../components/base/button/button";
import DateUtils from "../../../../../../core/services/date-utils";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import Api from "../../../../../../core/services/api/api";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import notebookTags from "../../../../../../core/models/static/routes/notebook-tags";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import {NCRTargetsTabs} from "../../../../../components/dialogs/upsert-ncr/sections/target";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import UpsertNCRDialog, {UpsertNCRProcessSteps} from "../../../../../components/dialogs/upsert-ncr";
import UpdateNcrStatusDialog from "../../../../../components/dialogs/update-ncr-status";
import {numComparator} from "../../../../../../core/services/utils/utils";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";
import {useHistory, useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import NCRSingleStatuses from "./statuses";
import CopyIconButton from "../../../../../components/base/copy-to-clipboard/copy-icon-button";


const NCRInfoSummaryBodyIds = {
    status: 'status',
    code: 'code',
    submittedDate: 'submissionDate',
    reportedByName: 'reportedByName',
    assigneeName: 'assigneeName',
    submittedByName: 'submittedByName',
}

const initialUpsertDialogState = {
    open: false,
    process: UpsertNCRProcessSteps.basicInfo,
}

const initialUpdateNcrStatusDialogState = {
    open: false,
    statusHistory: [],
    currentStatusId: null,
    currentStatusTitle: null,
    onSuccess: () => void 0,
}

const NCRSingle = () => {
    const params = useParams();
    const history = useHistory();
    const isMounted = useIsMounted();
    const [removing, setRemoving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState({});
    const [upsertDialogState, setUpsertDialogState] = useState(initialUpsertDialogState);
    const [updateNcrStatusDialogState, setUpdateNcrStatusDialogState] = useState(initialUpdateNcrStatusDialogState);
    const [notesLoading, setNotesLoading] = useState(true);
    const [notes, setNotes] = useState([]);
    const [noteTitleSuggestions, setNoteTitleSuggestions] = useState([]);
    const [actionsLoading, setActionsLoading] = useState(true);
    const [actions, setActions] = useState([]);
    const actionRefs = useRef({});
    const actionStateRefs = useRef({});
    const [taskStatuses, setTaskStatuses] = useState([]);
    const [ncrStatuses, setNcrStatuses] = useState([]);
    const [loadingStatuses, setLoadingStatuses] = useState(true);
    const dispatch = useDispatch()
    const operatorAccessLevels = useAccessLevels();

    const status = useMemo(() => report?.statuses?.find(e => e.isCurrentStatus), [report?.statuses]);

    const {configureNotebookTags, assignOnNoteUpsert} = useConfigureNotebook('ncr-information');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     * - Adds a listener to the note upsert event
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.ncrInformation(params.reportId, report?.code ?? ""));
        configureBehavioralTool((tags) => tags.ncrInformation(params.reportId, report?.code ?? ""));
        assignOnNoteUpsert((note, isDeleted) => {
            setNotes(prevState => {
                if (isDeleted) {
                    return prevState.filter(e => e.id !== note.id);
                }
                const index = prevState.findIndex(e => e.id === note.id);
                if (index === -1)
                    return [note, ...prevState];
                return prevState.map((e, i) => i === index ? note : e);
            });
        })
    }, [assignOnNoteUpsert, configureBehavioralTool, configureNotebookTags, params.reportId, report?.code]);

    /**
     * Fetches the statuses of tasks
     * @type {(function(): Promise<void>)|*}
     */
    const getTaskStatuses = useCallback(async () => {
        const response = await Api.getTaskStatuses();
        if (!isMounted())
            return
        setTaskStatuses(response?.data ?? []);
    }, [isMounted])

    /**
     * As soon as the component is mounted:
     * - fetches the title suggestions for the notes.
     */
    useEffect(() => {
        if (!(operatorAccessLevels.packages.ncr.tasks && operatorAccessLevels.packages.taskNote.tasks)) {
            return;
        }
        void getTaskStatuses();
    }, [getTaskStatuses, operatorAccessLevels.packages.ncr.tasks, operatorAccessLevels.packages.taskNote.tasks])

    /**
     * Fetches the statuses of NCR
     * @type {(function(): Promise<void>)|*}
     */
    const getNcrStatuses = useCallback(async () => {
        setLoadingStatuses(true);
        const response = await Api.listNcrStatuses();
        if (!isMounted())
            return
        if (response?.data?.length) {
            response.data.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
        }
        setNcrStatuses(response?.data ?? []);
        setLoadingStatuses(false);
    }, [isMounted])

    /**
     * As soon as the component is mounted:
     * - fetches the statuses for NCRs.
     */
    useEffect(() => {
        void getNcrStatuses();
    }, [getNcrStatuses])


    /**
     * Fetches the title suggestions for the notes.
     * @type {(function(): Promise<void>)|*}
     */
    const getTitleSuggestions = useCallback(async () => {
        const response = await Api.getNcrNoteTitleSuggestions();
        if (!isMounted())
            return
        setNoteTitleSuggestions(response?.data ?? []);
    }, [isMounted])

    /**
     * As soon as the component is mounted:
     * - fetches the title suggestions for the notes.
     */
    useEffect(() => {
        void getTitleSuggestions();
    }, [getTitleSuggestions])

    const firstActionId = useMemo(() => actions?.[0]?.id, [actions])
    /**
     * Opens the upsert task dialog after a status update, with the given status ID and title.
     * @type {(function(*, *): void)}
     */
    const openTaskDialogAfterStatusUpdate = useCallback((reportCode, statusTitle) => {
        if (!firstActionId) {
            return;
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertTask,
            {
                open: true,
                tags: notebookTags.ncrInformationActionGroup(params.reportId, firstActionId),
                isNCRTask: false,
                title: `Create Action for "${statusTitle ?? ""}"`,
                task: {title: `${statusTitle ?? ""} - ${reportCode?.toString() ?? ""}`},
                onSuccess: (task) => actionRefs.current?.[firstActionId]?.addTask(task),
            }))
    }, [
        firstActionId, dispatch, params.reportId
    ])

    /**
     * Opens the task dialog if the report is brand new (has only one status and no tasks yet).
     * @type {(function(*): Promise<void>)|*}
     */
    const openTaskDialogIfReportIsBrandNew = useCallback(async (report) => {
        const hasOneStatus = report?.statuses?.length === 1;
        if (!hasOneStatus) {
            return;
        }
        // await all action groups to load their tasks before opening the dialog
        await Promise.all(actions.map(action => actionStateRefs.current?.[action.id]?.loadedPromise ?? Promise.resolve()));
        const hasNoTasks = actions.every(action => (actionRefs.current?.[action.id]?.tasks?.length ?? 0) === 0);
        if (!hasNoTasks) {
            return;
        }
        // if we are here, it means that this report has just been created, and we can open the task dialog for the first status.
        const secondStatus = ncrStatuses?.[1];
        if (secondStatus) {
            openTaskDialogAfterStatusUpdate(report?.code, secondStatus.title);
        }
    }, [actions, ncrStatuses, openTaskDialogAfterStatusUpdate])

    /**
     * Fetches the report information from the server, and if the result of the api is successful, sets the report info.
     */
    const getReportInformation = useCallback(async () => {
        setLoading(true);
        const response = await Api.getNcrReport(params.reportId);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setReport(response?.data ?? {});
        }
        // Open task dialog if there is only one status, and no tasks yet (we can drive that this record has just been created)
        if (response?.resultFlag) {
            void openTaskDialogIfReportIsBrandNew(response?.data ?? {});
        }
    }, [isMounted, openTaskDialogIfReportIsBrandNew, params.reportId]);

    /**
     * Fetches the notes for the report from the server and sets the notes state.
     * @type {(function(): Promise<void>)|*}
     */
    const getNotesForReport = useCallback(async () => {
        setNotesLoading(true);
        let response = await Api.getPageNotes({
            pagination: DefaultPaginationInfo,
            tags: notebookTags.ncrInformation(params.reportId),
        });
        if (response?.resultFlag) {
            if (response?.data?.totalCount > response?.data?.items?.length) {
                response = await Api.getPageNotes({
                    pagination: {...DefaultPaginationInfo, pageSize: response?.data?.totalCount ?? 1},
                    tags: notebookTags.ncrInformation(params.reportId),
                });
            }
        }
        if (!isMounted())
            return;
        setNotesLoading(false);
        if (response?.resultFlag) {
            setNotes(response?.data?.items ?? []);
        }
    }, [isMounted, params.reportId]);

    /**
     * As soon as the component is mounted and with each change in the ID of the report.
     * - etches the action groups.
     */
    useEffect(() => {
        if (loadingStatuses) {
            return;
        }
        void getReportInformation();
    }, [getReportInformation, loadingStatuses])

    /**
     * Resets the action group refs when the report ID changes.
     */
    useLayoutEffect(() => {
        actionRefs.current = {};
        actionStateRefs.current = {};
    }, [params.reportId])

    /**
     * As soon as the component is mounted, fetches the notes for the report.
     */
    useEffect(() => {
        if (!(operatorAccessLevels.packages.ncr.notes && operatorAccessLevels.packages.taskNote.notes)) {
            return;
        }
        void getNotesForReport();
    }, [getNotesForReport, operatorAccessLevels.packages.ncr.notes, operatorAccessLevels.packages.taskNote.notes])

    /**
     * Fetches the action groups for the report from the server and sets the actions state.
     * @type {(function(): Promise<void>)|*}
     */
    const getActionGroups = useCallback(async () => {
        setActionsLoading(true);
        const response = await Api.listNcrActionGroups();
        if (!isMounted())
            return;
        setActionsLoading(false);
        if (response?.resultFlag) {
            const actions = response?.data ?? [];
            actions.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
            setActions(actions);
        }
    }, [isMounted])

    /**
     * As soon as the component is mounted:
     * - etches the action groups.
     */
    useEffect(() => {
        if (!(operatorAccessLevels.packages.ncr.tasks && operatorAccessLevels.packages.taskNote.tasks)) {
            return;
        }
        void getActionGroups();
    }, [getActionGroups, operatorAccessLevels.packages.ncr.tasks, operatorAccessLevels.packages.taskNote.tasks])

    /**
     * Opens the upsert dialog with the current report.
     * @type {(function(): void)|*}
     */
    const openUpdateStatusDialog = useCallback(() => {
        const reportCode = report?.code;
        setUpdateNcrStatusDialogState({
            open: true,
            statusHistory: report?.statuses ?? [],
            statusId: status?.statusId,
            onSuccess: (statusId, statusTitle, formId, formResponseId) => {
                const indexOfStatus = ncrStatuses?.findIndex(e => e.id === statusId);
                const hasNextStatus = indexOfStatus !== -1 && indexOfStatus < (ncrStatuses?.length ?? 0) - 1;
                setUpdateNcrStatusDialogState(initialUpdateNcrStatusDialogState);
                setUpsertDialogState({
                    open: true,
                    report: {...report, statusId: statusId},
                    disableNavigatingBetweenSteps: true,
                    process: UpsertNCRProcessSteps.questions,
                    formId: formId,
                    formResponseId: formResponseId,
                    onSuccess: (report, formId, formResponseId) => {
                        if (hasNextStatus && !ncrStatuses?.[indexOfStatus + 1]?.title?.toLowerCase().startsWith('need ')) {
                            openTaskDialogAfterStatusUpdate(reportCode, ncrStatuses?.[indexOfStatus + 1]?.title);
                        }
                        setReport(prevState => ({
                            ...prevState,
                            ...report,
                            statuses: [
                                ...prevState.statuses.map(e => ({...e, isCurrentStatus: false})),
                                {
                                    statusId: statusId,
                                    statusTitle: statusTitle,
                                    isCurrentStatus: true,
                                    formReference: formId,
                                    formResponseReference: formResponseId,
                                    submitDateTime: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
                                }
                            ],
                        }));
                    }
                })
            }
        })
    }, [ncrStatuses, openTaskDialogAfterStatusUpdate, report, status?.statusId])

    /**
     * Opens the upsert dialog with the current report.
     * @type {(function(): void)|*}
     */
    const updateSummaryInfo = useCallback(() => {
        setUpsertDialogState({
            open: true,
            report: report,
            disableNavigatingBetweenSteps: true,
            process: UpsertNCRProcessSteps.basicInfo,
            onSuccess: (report) => {
                setReport(report);
            }
        })
    }, [report]);

    /**
     * Opens the upsert dialog with the current report.
     * @type {(function(): void)}
     */
    const updateQuestions = useCallback((onSuccess) => {
        setUpsertDialogState({
            open: true,
            report: report,
            disableNavigatingBetweenSteps: true,
            process: UpsertNCRProcessSteps.questions,
            formId: status?.formReference,
            formResponseId: status?.formResponseReference,
            onSuccess: (report) => {
                setReport(report);
                onSuccess(status?.formReference, status?.formResponseReference);
            }
        })
    }, [report, status?.formReference, status?.formResponseReference]);

    /**
     * Opens the upsert dialog with the current report.
     * @type {(function(): void)|*}
     */
    const updateTargets = useCallback((products = false) => {
        setUpsertDialogState({
            open: true,
            report: report,
            disableNavigatingBetweenSteps: true,
            process: UpsertNCRProcessSteps.target,
            targetsTab: products ? NCRTargetsTabs.products : NCRTargetsTabs.process,
            onSuccess: (report) => {
                setReport(report);
            }
        })
    }, [report]);

    /**
     * Removes the given note from the notes of the NCR's in the server.
     * @param noteOrNotes
     * @return {Promise<void>}
     */
    const removeNote = useCallback(async (note) => {
        const response = await Api.removeNote(note.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setNotes(prevState => prevState.filter(e => e.id !== note.id));
        }
    }, [isMounted])

    /**
     * Opens the upsert dialog with the given note.
     * @type {(function(*): void)|*}
     */
    const openUpsertNoteDialog = useCallback((note) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote, {
            open: true,
            note: note,
            tagList: notebookTags.ncrInformation(params.reportId, report?.code),
            defaultIsPrivate: false,
            titleSuggestions: noteTitleSuggestions.map(e => e.title),
            onSuccess: (note) => setNotes(prevState => {
                const index = prevState.findIndex(e => e.id === note.id);
                if (index === -1)
                    return [note, ...prevState];
                return prevState.map((e, i) => i === index ? {...e, ...note} : e);

            }),
        }))
    }, [dispatch, noteTitleSuggestions, params.reportId, report?.code])

    const NCRInfoSummary = useMemo(() => ({
        body: [
            {
                id: NCRInfoSummaryBodyIds.code,
                title: 'Code',
                value: report?.code,
                editable: operatorAccessLevels.packages.ncr.manageReport,
                openDialog: operatorAccessLevels.packages.ncr.manageReport,
                clearable: false,
                edit: updateSummaryInfo,
                copyable: true,
            },
            {
                id: NCRInfoSummaryBodyIds.submittedDate,
                title: 'Submission Date',
                value: report?.submitDateTime,
                format: (value) => !value ? '--' : DateUtils.server(value).format(DateUtils.dateTimeFormat),
                editable: operatorAccessLevels.packages.ncr.manageReport,
                openDialog: operatorAccessLevels.packages.ncr.manageReport,
                clearable: false,
                edit: updateSummaryInfo,
            },
            {
                id: NCRInfoSummaryBodyIds.reportedByName,
                title: 'Reported By',
                value: !report?.reportedByOperator ? '--' : [report?.reportedByOperator?.firstname, report?.reportedByOperator?.lastname].filter(Boolean).join(' '),
                editable: operatorAccessLevels.packages.ncr.manageReport,
                openDialog: operatorAccessLevels.packages.ncr.manageReport,
                clearable: false,
                edit: updateSummaryInfo,
            },
            // {
            //     id: NCRInfoSummaryBodyIds.assigneeName,
            //     title: "Assigned To",
            //     value: !report?.assignedToOperator ? '--' : [report?.assignedToOperator?.firstname, report?.assignedToOperator?.lastname].filter(Boolean).join(' '),
            //     editable: operatorAccessLevels.packages.ncr.manageReport,
            //     openDialog: operatorAccessLevels.packages.ncr.manageReport,
            //     clearable: false,
            //     edit: updateSummaryInfo,
            // },
            {
                id: NCRInfoSummaryBodyIds.submittedByName,
                title: "Submitted By",
                value: !report?.createdByOperator ? '--' : [report?.createdByOperator?.firstname, report?.createdByOperator?.lastname].filter(Boolean).join(' '),
                editable: false,
                clearable: false,
                edit: updateSummaryInfo,
            },
        ],
    }), [report?.code, report?.submitDateTime, report?.reportedByOperator, report?.createdByOperator, operatorAccessLevels.packages.ncr.manageReport, updateSummaryInfo
        // report?.assignedToOperator,
    ])

    /**
     * Returns the number of completed answers
     * @type {(function(): Promise<void>)}
     */
    const removeNcr = useCallback(async () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this report?
                </p>
            </div>,
            {maxWidth: 'sm'},
            async (v) => {
                if (!v)
                    return;
                setRemoving(true);
                const response = await Api.removeNcrReport(report.id);
                if (!isMounted())
                    return;
                setRemoving(false);
                if (response?.resultFlag) {
                    history.replace(routes.ncr.reports);
                }
            }
        );
    }, [report?.id, isMounted, history])

    /**
     * Adds the given ref to the action group with the given ID.
     * @type {(function(*, *))|*}
     */
    const addActionGroupRef = useCallback((id, ref) => {
        actionRefs.current = {
            ...actionRefs.current,
            [id]: ref,
        }
    }, []);

    /**
     * Sets the state ref for the action group with the given ID.
     * @type {(function(string, Record<string, any>): void)}
     */
    const setActionGroupStateRef = useCallback((id, updates) => {
        const prevState = actionStateRefs.current?.[id] ?? {};
        const newState = {
            ...(prevState),
            ...(updates ?? {}),
            ...(typeof updates?.loaded === 'boolean' && {
                loaded: updates.loaded === false && prevState.loaded === true ? true : updates?.loaded,
            }),
        };
        if (newState.loaded === false) {
            // since we have started the loading, create the promise that we will be awaiting in our fetch report info function.
            newState.loadedPromise = new Promise((resolve) => {
                newState.resolveLoaded = resolve;
            });
        } else if (newState.loaded === true) {
            // since we have finished the loading, resolve the promise that was created when the loading started.
            prevState.resolveLoaded?.();
            newState.resolveLoaded = null;
        }
        actionStateRefs.current = {
            ...actionStateRefs.current,
            [id]: newState,
        }
    }, []);

    return (
        <>
            <Row noGutters className={"NCR-single"}>
                <Col xs={12} className={'d-flex align-items-center justify-content-between mb-4'}>
                    <div className={'d-flex align-items-center'}>
                        <IconButton
                            onPressed={() => history.goBack()}
                            color={'paper.secondary'}
                        >
                            <ArrowBack className='icon normal'/>
                        </IconButton>
                        <p className={'view-title ml-2'}>
                            NCR Report Information {report?.code ?? ''}
                            {
                                !!report?.code &&
                                <CopyIconButton value={report.code}/>
                            }
                        </p>
                    </div>

                    {
                        !loading && operatorAccessLevels.packages.ncr.manageReport &&
                        <Button
                            className='d-flex align-items-center justify-content-center mb-3'
                            variant={'outlined'}
                            disabled={removing || notesLoading}
                            color={'error'}
                            onClick={removeNcr}
                        >
                            <DeleteOutlined sx={{marginInlineEnd: 0.25}}/>
                            {
                                removing
                                    ? "Removing..."
                                    : "Remove this NCR"
                            }
                        </Button>
                    }
                </Col>
                {
                    loading &&
                    <>
                        <Col xs={12}>
                            <LoadingLottieAnimation
                                width={'min(80%, 400px)'}
                                className={'my-5 py-5'}
                            />
                        </Col>
                    </>
                }
                {
                    !loading &&
                    <Row noGutters className={'w-100'}>
                        <Col xs={12} lg={8} className={`NCR-summary-info-card-container pr-lg-3`}>
                            <Box
                                sx={{
                                    backgroundColor: 'background.paper',
                                    borderRadius: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%'
                                }}
                            >
                                <SummaryInfoCard
                                    body={NCRInfoSummary.body}
                                    style={{padding: '1rem 0.5rem'}}
                                />
                                <Row
                                    noGutters xs={12}
                                    className="d-flex my-2"
                                    style={{paddingLeft: "0.5rem", paddingRight: "0.5rem"}}
                                >
                                    <>
                                        <div className={'d-flex w-100 align-items-start'}>
                                            <Col xs className={''}>
                                                <p className={"card-headers tertiary-main mb-0"}>
                                                    Description
                                                </p>
                                            </Col>
                                            <Col xs={'auto'} className="d-flex justify-content-end p-0">
                                                {
                                                    operatorAccessLevels.packages.ncr.manageReport &&
                                                    <IconButton
                                                        className={'mx-1'}
                                                        color={'secondary'}
                                                        onPressed={() => updateSummaryInfo()}
                                                    >
                                                        <Edit className={'icon small'}/>
                                                    </IconButton>
                                                }
                                            </Col>
                                        </div>
                                        <Col xs={12}>
                                            <p className={'text-sm px-3 pt-2'}>
                                                {report?.shortDescription ?? ""}
                                            </p>
                                        </Col>
                                    </>
                                </Row>
                                <Row
                                    noGutters xs={12}
                                    className="d-flex my-2"
                                    style={{paddingLeft: "0.5rem", paddingRight: "0.5rem"}}
                                >
                                    <div className={'d-flex w-100 align-items-start'}>
                                        <Col xs className={''}>
                                            <p className={"card-headers tertiary-main mb-0"}>
                                                RGA
                                            </p>
                                        </Col>
                                        <Col xs={'auto'} className="d-flex justify-content-end p-0">
                                            {
                                                operatorAccessLevels.packages.ncr.manageReport &&
                                                <IconButton
                                                    className={'mx-1'}
                                                    color={'secondary'}
                                                    onPressed={() => updateSummaryInfo()}
                                                >
                                                    <Edit className={'icon small'}/>
                                                </IconButton>
                                            }
                                        </Col>
                                    </div>
                                    <Col xs={12}>
                                        <p className={'text-sm px-3 pt-2'}>
                                            {report?.rgaNumber ?? ""}
                                        </p>
                                    </Col>
                                </Row>
                                <Row
                                    noGutters xs={12}
                                    className="d-flex my-2"
                                    style={{paddingLeft: "0.5rem", paddingRight: "0.5rem"}}
                                >
                                    <div className={'d-flex w-100 align-items-start'}>
                                        <Col xs className={''}>
                                            <p className={"card-headers tertiary-main mb-0"}>
                                                Target Processes
                                            </p>
                                        </Col>
                                        <Col xs={'auto'} className="d-flex justify-content-end p-0">
                                            {
                                                operatorAccessLevels.packages.ncr.manageReport &&
                                                <IconButton
                                                    className={'mx-1'}
                                                    color={'secondary'}
                                                    onPressed={() => updateTargets(false)}
                                                >
                                                    <Edit className={'icon small'}/>
                                                </IconButton>
                                            }
                                        </Col>
                                    </div>
                                    <div className={"d-flex flex-wrap w-100 p-2"}>
                                        {
                                            report?.processes?.map((process) => (
                                                <Chip
                                                    className={"mr-2 mb-2"}
                                                    key={process?.id}
                                                    label={process?.title}
                                                />
                                            ))
                                        }
                                    </div>
                                </Row>
                                <Row
                                    noGutters xs={12}
                                    className="d-flex my-2"
                                    style={{paddingLeft: "0.5rem", paddingRight: "0.5rem"}}
                                >
                                    <div className={'d-flex w-100 align-items-start'}>
                                        <Col xs className={''}>
                                            <p className={"card-headers tertiary-main mb-0"}>
                                                Target products
                                            </p>
                                        </Col>
                                        <Col xs={'auto'} className="d-flex justify-content-end p-0">
                                            {
                                                operatorAccessLevels.packages.ncr.manageReport &&
                                                <IconButton
                                                    className={'mx-1'}
                                                    color={'secondary'}
                                                    onPressed={() => updateTargets(true)}
                                                >
                                                    <Edit className={'icon small'}/>
                                                </IconButton>
                                            }
                                        </Col>
                                    </div>
                                    <div className={"d-flex flex-wrap w-100 p-2"}>
                                        {
                                            report?.parts?.map((part) => (
                                                <Chip
                                                    className={"mr-2 mb-2"}
                                                    key={part?.id}
                                                    sx={{
                                                        backgroundColor: 'background.default',
                                                    }}
                                                    label={
                                                        <ProductInformationPopover partNo={part?.partNumber ?? part?.partNo}>
                                                            {part?.partNumber ?? part?.partNo ?? ''}
                                                        </ProductInformationPopover>
                                                    }
                                                />
                                            ))
                                        }
                                    </div>
                                </Row>
                            </Box>
                            <Box sx={{
                                backgroundColor: 'background.paper',
                                borderRadius: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                marginBlockStart: '1rem',
                            }}>
                                <Row noGutters className="d-flex my-2" style={{paddingLeft: "0.5rem", paddingRight: "0.5rem"}}>
                                    <div className={'d-flex w-100 align-items-center'}>
                                        <Col xs>
                                            <p className={"card-headers tertiary-main mb-0"}>
                                                Status
                                            </p>
                                        </Col>
                                        <Col xs={'auto'} className="d-flex justify-content-end p-0">
                                            {
                                                operatorAccessLevels.packages.ncr.manageReport &&
                                                <Button
                                                    className='d-flex align-items-center justify-content-center'
                                                    variant={'outlined'}
                                                    disabled={removing || notesLoading || loading}
                                                    color={'primary'}
                                                    onClick={() => openUpdateStatusDialog()}
                                                >
                                                    <Add className={'mr-2'}/>
                                                    <p>
                                                        Change Status
                                                    </p>
                                                </Button>
                                            }
                                        </Col>
                                    </div>
                                    <NCRSingleStatuses
                                        statuses={report?.statuses}
                                        createdAt={report?.reportedDateTime}
                                        updateQuestions={updateQuestions}
                                        // isSummaryCardToggled={isSummaryCardToggled}
                                    />
                                </Row>
                            </Box>
                        </Col>
                        <Col xs={12} lg={4} className={'mt-lg-1 d-flex flex-column pr-lg-2'}>
                            {
                                operatorAccessLevels.packages.ncr.notes &&
                                operatorAccessLevels.packages.taskNote.notes &&
                                <>
                                    <Box className={'ncr-notes-view'}>
                                        <Row noGutters xs={12}>
                                            <Col xs={4}>
                                                <Typography variant={'body1'} sx={{fontWeight: 600}}>
                                                    Notes
                                                </Typography>
                                            </Col>
                                            <Col xs={8} className={'d-flex justify-content-end'}>
                                                <Button
                                                    className='d-flex align-items-center justify-content-center'
                                                    variant={'outlined'}
                                                    disabled={removing || notesLoading || loading}
                                                    color={'primary'}
                                                    onClick={() => openUpsertNoteDialog()}
                                                >
                                                    <Add className={'mr-2'}/>
                                                    <p>
                                                        Add a new Note
                                                    </p>
                                                </Button>
                                            </Col>
                                        </Row>
                                        <div className={"notes-list"}>
                                            {
                                                notesLoading &&
                                                Array.from({length: 3}).map((_, index) => (
                                                    <div key={index} className={'pl-0'} style={{display: 'inline-block'}}>
                                                        <div className={'loading extraLarge'}>
                                                            <div/>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                            {
                                                !notesLoading &&
                                                notes?.map((note) => (
                                                    <div key={note?.id} className={'pl-0'} style={{display: 'inline-block'}}>
                                                        <NcrNoteCard
                                                            isNotesPage={false}
                                                            note={note}
                                                            update={openUpsertNoteDialog}
                                                            remove={removeNote}
                                                        />
                                                    </div>
                                                ))
                                            }
                                            {
                                                !notesLoading && !notes.length &&
                                                <p className={'text-sm text-color-2 text-center w-100 my-5'}>
                                                    No notes have been added to this report.
                                                </p>
                                            }
                                        </div>
                                    </Box>
                                </>
                            }
                            {
                                operatorAccessLevels.packages.ncr.tasks &&
                                operatorAccessLevels.packages.taskNote.tasks &&
                                <>
                                    {
                                        actionsLoading &&
                                        Array.from({length: 3}).map((_, index) => (
                                            <Box className={"mb-4"} key={index}>
                                                <div className={'loading new-bulk-price-change'}>
                                                    <div/>
                                                </div>
                                            </Box>
                                        ))
                                    }
                                    {
                                        !actionsLoading &&
                                        actions?.map((action) => (
                                            <Box className={"mb-4"} key={action?.id}>
                                                <NCRInformationActionTable
                                                    ref={(ref) => addActionGroupRef(action?.id, ref)}
                                                    action={action}
                                                    statuses={taskStatuses}
                                                    ncrCode={report?.code}
                                                    onLoadingInitiated={() => setActionGroupStateRef(action?.id, {loaded: false})}
                                                    onTasksLoaded={(total) => setActionGroupStateRef(action?.id, {total: total, loaded: true})}
                                                />
                                            </Box>
                                        ))
                                    }
                                </>
                            }
                        </Col>
                    </Row>
                }
            </Row>
            <UpdateNcrStatusDialog
                {...updateNcrStatusDialogState}
                setOpen={() => setUpdateNcrStatusDialogState(initialUpdateNcrStatusDialogState)}
            />
            <UpsertNCRDialog
                {...upsertDialogState}
                setOpen={() => setUpsertDialogState(initialUpsertDialogState)}
            />
        </>
    )
};

export default NCRSingle;
